import { computed, reactive, readonly, ref } from '@vue/reactivity';
import { v4, validate as isUuid } from 'uuid';
import type { RootStore } from './rootStore';
import type {
  IDefaultOfflineItemModel,
  IFeathersService,
  IKeyValueStore,
  IServiceStoreOptions,
  IServiceStoreState,
  IServiceStoreSyncState,
  ISetItemOptions,
  TApiError,
  IFeathersQuery,
  IServiceFindResponse,
  TFeathersEventTypes,
} from './types';
import { chunkArray } from './utils/chunkArray';
import { AUTH_STATE_CHANGED, ONLINE_STATE_CHANGED } from './constants';
import { Console } from './utils/debugger';

export class ServiceStore<ItemModel extends IDefaultOfflineItemModel, CreateItemModel = any> {
  // field configs
  idField = '_id';
  uuidField = 'uuid';
  onServerAtField = 'onServerAt';
  onClientAtField = 'onClientAt';
  deletedAtField = 'deletedAt';
  // updateField = '$$update';
  historyField = '$$history';
  // configurations
  offlineMode = true;
  historyMode = true;
  service: IFeathersService<ItemModel>;
  serviceName: string;
  rootStore: RootStore;
  storage: IKeyValueStore;
  storageKey: string;
  // methods
  onEvent?: (event: string, payload: ItemModel) => void;
  // items state
  currentItemId = ref<string | null>(null);
  item = computed(() =>
    this.state.items.find((item) => item._id === this.currentItemId.value || item.uuid === this.currentItemId.value),
  );
  state: IServiceStoreState<ItemModel> = reactive({
    items: [],
  });
  items = computed(() => this.state.items.slice());
  // syncs
  syncing: IServiceStoreSyncState = reactive({
    todo: 0,
    done: 0,
    error: [],
  });
  // loadings
  isFindPending = ref(false);
  isGetPending = ref(false);
  isCreatePending = ref(false);
  isUpdatePending = ref(false);
  isPatchPending = ref(false);
  isRemovePending = ref(false);
  isSyncPending = computed(() => this.syncing.done < this.syncing.todo);
  // errors
  findError = ref<TApiError>(undefined);
  getError = ref<TApiError>(undefined);
  createError = ref<TApiError>(undefined);
  updateError = ref<TApiError>(undefined);
  patchError = ref<TApiError>(undefined);
  removeError = ref<TApiError>(undefined);
  syncErrors = computed(() => this.syncing.error.slice());
  debug = false;
  console: ReturnType<typeof Console>;
  // hooks
  beforeSync = () => {
    this.console.log('Hook beforeSync', this.serviceName);
  };
  afterSync = () => {
    this.console.log('Hook afterSync', this.serviceName);
  };
  afterSet = (items: ItemModel[]) => {
    this.console.log('Hook afterSet', this.serviceName, items);
  };

  constructor(rootStore: RootStore, options: IServiceStoreOptions) {
    this.rootStore = rootStore;
    this.service = options.feathers.service(options.service);
    this.serviceName = options.service;
    this.storage = options.storage;
    this.storageKey = `@storage/${options.service}`;

    if (options.config) {
      this.offlineMode = options.config.offlineMode === false ? false : true;
      this.idField = options.config.idField || '_id';
      this.uuidField = options.config.uuidField || 'uuid';
      this.onServerAtField = options.config.onServerAtField || 'onServerAt';
      this.onClientAtField = options.config.onClientAtField || 'onClientAt';
      this.deletedAtField = options.config.deletedAtField || 'deletedAt';
      this.onEvent = options.config.onEvent || undefined;
      this.debug = options.config.debug || false;
    }

    if (options.beforeSync) {
      this.beforeSync = options.beforeSync;
    }

    if (options.afterSync) {
      this.afterSync = options.afterSync;
    }

    if (options.afterSet) {
      this.afterSet = options.afterSet;
    }

    this.console = Console(this.debug);
    this.initialize();
    this.rootStore.registerService(options.service);
    this.listenToEvents();
    /*
      TODO:
      implement watcher to online / authenticated state in root
      to start sync if online goes from "false" to "true"
    */
  }

  async initialize() {
    /*
      - get items from storage
      - set items to state
      - if online => sync()
    */
    const storedItems = await this.storage.getItem(this.storageKey);
    if (storedItems) {
      this.state.items = JSON.parse(storedItems);
      if (this.rootStore.isOnline.value && this.offlineMode && this.rootStore.isAuthenticated.value) {
        this.sync(this.state.items);
      }
    }
  }

  removeListener(event: TFeathersEventTypes): void {
    return this.service.removeAllListeners(event);
  }

  listenToEvents() {
    const listenToWsEvents = () => {
      this.removeListener('created');
      this.removeListener('patched');
      this.removeListener('updated');
      this.removeListener('removed');
      this.service.on('created', (payload) => this.on('created', payload));
      this.service.on('patched', (payload) => this.on('patched', payload));
      this.service.on('updated', (payload) => this.on('updated', payload));
      this.service.on('removed', (payload) => this.on('removed', payload));
      this.console.log(this.serviceName, 'listening to WebSocket Events.');
    };

    const onlineStateChanged = (isOnline?: boolean) => {
      if (isOnline) {
        listenToWsEvents();
        this.initialize();
      }
    };

    const authStateChanged = (isAuthed?: boolean) => {
      if (!isAuthed) {
        this.reset();
      }
    };

    this.console.log(this.serviceName, 'listening to RootStore Events.');
    this.rootStore.emitter.off<boolean>(ONLINE_STATE_CHANGED, (e) => onlineStateChanged(e));
    this.rootStore.emitter.off<boolean>(AUTH_STATE_CHANGED, (e) => authStateChanged(e));
    this.rootStore.emitter.on<boolean>(ONLINE_STATE_CHANGED, (e) => onlineStateChanged(e));
    this.rootStore.emitter.on<boolean>(AUTH_STATE_CHANGED, (e) => authStateChanged(e));
    if (this.rootStore.isOnline.value) {
      listenToWsEvents();
    }
  }

  // state manipulation methods
  setItems(items: ItemModel[], options: ISetItemOptions = {}): number {
    if (options.refresh) {
      this.state.items = [];
      this.state.items = items;
      return items.length;
    }
    const stateItems = this.state.items;
    this.console.time(`Save ${items.length} items in ${this.serviceName}`);
    items.forEach((item) => {
      let isInStateIndex = -1;
      if (this.state.items.length > 0) {
        // check if item with same id exists
        isInStateIndex = stateItems.findIndex(
          (stateItem) =>
            (this.offlineMode && stateItem[this.uuidField] === item[this.uuidField]) ||
            (item[this.idField] && stateItem[this.idField] === item[this.idField]),
        );
      }
      if (isInStateIndex !== -1) {
        stateItems[isInStateIndex] = item;
      } else {
        stateItems.push(item);
      }
    });
    this.state.items = stateItems;
    this.storage.setItem && this.storage.setItem(this.storageKey, JSON.stringify(this.state.items));
    this.console.timeEnd(`Save ${items.length} items in ${this.serviceName}`);
    this.afterSet(this.state.items);
    return this.state.items.length;
  }

  removeItem(id: string) {
    const idField = isUuid(id) ? this.uuidField : this.idField;
    const index = this.state.items.findIndex((item) => item[idField] === id);
    this.console.log('Remove Item', this.serviceName, index, id);
    if (index !== -1) {
      this.state.items.splice(index, 1);
    }
    this.storage.setItem && this.storage.setItem(this.storageKey, JSON.stringify(this.state.items));
  }

  reset() {
    this.storage.removeItem && this.storage.removeItem(this.storageKey);
    this.state.items = [];
    this.console.log('Reset', this.serviceName);
  }

  // service methods
  create(data: CreateItemModel) {
    /*
      - set isCreatePending true
      - generate uuid
      - generate onClientAt DateTime
      - create item from createItemModel
      - save item to state.items
      - set isCreatePending false
      - run _netcreate if RootStore.online
    */
    this.isCreatePending.value = true;
    if (this.offlineMode) {
      const uuid = v4();
      const onClientAt = new Date().toISOString();
      const item: CreateItemModel = {
        ...data,
      };
      this.console.groupCollapsed(`Create offline first in ${this.serviceName}`);
      this.console.time(`Create ${uuid}`);
      // @ts-ignore
      item[this.uuidField] = uuid;
      // @ts-ignore
      item[this.onClientAtField] = onClientAt;
      // @ts-ignore
      const $$create = { ...item };
      this.console.log('Data:', item);
      // @ts-ignore
      this.setItems([{ ...item, $$create }]);
      this.isCreatePending.value = false;
      this.console.timeEnd(`Create ${uuid}`);
      if (this.rootStore.isOnline.value) {
        this.console.log(`Client is online, direct sync`, item);
        this._netcreate(item);
      }
      this.console.groupEnd();
    } else {
      this.console.log(this.serviceName, 'Create', data);
      this._netcreate(data);
    }
  }

  find(query: IFeathersQuery = {}, options?: ISetItemOptions) {
    /*
      - set isFindPending true
      - run _netfind if RootStore.online
      - save items to state.items => setItems
      - set isFindPending false
      - autopaginate from state.items length
    */
    if (this.rootStore.isOnline.value) {
      this.isFindPending.value = true;
      const queryParams: IFeathersQuery = {};
      if (!options?.refresh) {
        queryParams.$skip = this.state.items.filter((item) => item._id).length;
        queryParams.$limit = query.$limit || 30;
      } else {
        query.$skip = 0;
        query.$limit = query.$limit || 30;
      }
      this._netfind({ ...queryParams, ...query });
    }
  }

  get(id: string, params: IFeathersQuery = {}) {
    /*
      - set isGetPending true
      - run _netget if RootStore.online
      - save item to state.items => setItems
      - set isGetPending false
    */
    if (this.rootStore.isOnline.value) {
      this.isGetPending.value = true;
      this._netget(id, params);
    }
  }

  /**
   *
   *
   * @param {string} id
   * @param {ItemModel} data
   * @param {*} [params={}]
   * @description Don't supports history and offline mode. Use .patch(...) instead!
   * @memberof ServiceStore
   */
  update(id: string, data: Partial<ItemModel | Omit<IDefaultOfflineItemModel, '$$patch' | '$$history'>>, params = {}) {
    /*
      - only run if RootStore.online
      - set isUpdatePending true
      - save item to state.items
      - set isUpdatePending false
      - run _netupdate
    */
    if (this.rootStore.isOnline.value) {
      this.isUpdatePending.value = true;
      this._netupdate(id, data, params);
    }
  }

  patch(id: string, data: Partial<ItemModel | Omit<IDefaultOfflineItemModel, '$$patch' | '$$history'>>, params = {}) {
    /*
      - set isPatchPending true
      - generate onClientAt DateTime
      - set $$patch value in item if _id !== null || undefined
      - update $$history value in item
      - save item to state.items
      - set isPatchPending false
      - run _netpatch if RootStore.online
    */
    if (this.offlineMode) {
      this.isPatchPending.value = true;
      const idField = isUuid(id) ? this.uuidField : this.idField;
      const currentItemIndex = this.state.items.findIndex((item) => item[idField] === id);
      if (currentItemIndex !== -1) {
        const currentItem = this.state.items[currentItemIndex];
        const onClientAt: string =
          data[this.onClientAtField] && data[this.onClientAtField] !== currentItem[this.onClientAtField]
            ? data[this.onClientAtField]
            : new Date().toISOString();
        const patchObj = {
          ...data,
          onClientAt,
        };
        this.setItems([
          {
            ...this.state.items[currentItemIndex],
            ...patchObj,
            $$patch: {
              ...(this.state.items[currentItemIndex].$$patch || {}),
              ...patchObj,
            },
            $$history: {
              [onClientAt]: currentItem,
              ...(this.state.items[currentItemIndex].$$history || {}),
            },
          },
        ]);
        this.isPatchPending.value = false;
      }
      if (this.rootStore.isOnline.value) {
        this._netpatch(id, data, params);
      }
    } else if (this.rootStore.isOnline.value) {
      this.isPatchPending.value = true;
      this._netpatch(id, data, params);
    }
  }

  // TODO: Implement force logic
  remove(id: string, params = {} /*, force = false*/) {
    /*
      - if offlineMode:
        - run this.patch with deletedAt and onClientAt
      - else
        - run _netremove
    */
    if (this.offlineMode) {
      const idField = isUuid(id) ? this.uuidField : this.idField;
      const now = new Date().toISOString();
      const item = this.state.items.find((item) => item[idField] === id);
      if (!item?.$$create) {
        this.patch(id, {
          [this.deletedAtField]: now,
          [this.onClientAtField]: now,
        });
      } else {
        this.removeItem(id);
      }
    } else if (this.rootStore.isOnline.value) {
      this.isRemovePending.value = true;
      this._netremove(id, params);
    }
  }

  sync(items: ItemModel[]) {
    /*
      - if !item.$$patch && !item.deletedAt
        - add to array of getNewData => find with query = { _id: { $in: getNewData } }
      - if item.$$create
        - _netcreate
        - if item.$$patch
          - _netpatch
      - if item.$$patch && !item.$$patch.deletedAt
        - _netpatch
      - if item.$$patch && item.$$patch.deletedAt
        - _netpatch but only onClientAt and deletedAt
    */
    this.beforeSync();
    this.console.groupCollapsed(`Sync ${this.serviceName}`);
    this.console.time(`Sync Duration ${this.serviceName}`);
    this.syncing.todo = items.length;
    this.syncing.done = 0;
    this.syncing.error = [];
    const findIds: string[] = [];
    const stateItems = readonly(items);

    const forEachReady = () => {
      if (this.rootStore.isOnline.value) {
        const chunks: string[][] = chunkArray(findIds, 20);
        chunks.forEach((ids, index) => {
          this.console.log(`Chunk No. ${index + 1}`, ids);
          this._netfind({
            filter: {
              _id: {
                $in: ids,
              },
            },
          }).then(() => {
            this.syncing.done += ids.length;
            if (index + 1 === chunks.length) {
              this.console.log(
                'Sync results',
                this.serviceName,
                `${this.syncing.done} / ${this.syncing.todo} synced`,
                `${this.syncing.error.length} Errors`,
              );
              this.console.timeEnd(`Sync Duration ${this.serviceName}`);
              this.afterSync();
            }
          });
        });
        if (chunks.length === 0) {
          this.console.log(
            'Sync results',
            this.serviceName,
            `${this.syncing.done} / ${this.syncing.todo} synced`,
            `${this.syncing.error.length} Errors`,
          );
          this.console.timeEnd(`Sync Duration ${this.serviceName}`);
          this.afterSync();
        }
        this.console.groupEnd();
      }
    };

    stateItems.forEach((item, index) => {
      if (this.rootStore.isOnline.value) {
        if (!item.$$patch && !item.$$create && item._id) {
          // item that was not changed offline
          findIds.push(item._id);
          this.console.log('No offline changes for', item._id);
        } else if (item.$$create) {
          // offline created item
          this.syncing.todo++;
          this.console.time(`SYNC CREATE ${item.uuid}`);
          // @ts-ignore
          this._netcreate({
            ...item.$$create,
          })
            .then((createdItem) => {
              this.console.log('Sync create for success', item.uuid);
              this.console.timeEnd(`SYNC CREATE ${item.uuid}`);
              if (item.$$patch) {
                this.console.log('Sync patch of offline created item', item.$$patch);
                this.console.time(`SYNC PATCH AFTER CREATE ${item.uuid}`);
                this._netpatch(createdItem._id, {
                  ...item.$$patch,
                })
                  .then(() => {
                    this.console.log('Sync patch success');
                    this.console.timeEnd(`SYNC PATCH AFTER CREATE ${item.uuid}`);
                    this.syncing.done++;
                  })
                  .catch(() => {
                    this.console.log('Sync patch error');
                    this.console.timeEnd(`SYNC PATCH AFTER CREATE ${item.uuid}`);
                    console.log('SYNC PATCH AFTER CREATE SOFTERROR', createdItem._id);
                    this.syncing.done++;
                    this.patchError.value = undefined;
                  });
              } else {
                this.syncing.done++;
              }
            })
            .catch((e) => {
              this.console.log('Sync create error');
              this.console.timeEnd(`SYNC CREATE ${item.uuid}`);
              this.syncing.done++;
              this.syncing.error.push({
                ...readonly(item),
                $$error: e,
              });
            });
        } else if (item.$$patch && !item.$$patch.deletedAt) {
          // offline patched item but not deleted
          this.syncing.todo++;
          this.console.time(`SYNC PATCH ${item._id}`);
          this._netpatch(item._id, {
            ...item.$$patch,
          })
            .then(() => {
              this.console.time(`SYNC PATCH ${item._id}`);
              this.syncing.done++;
              this.console.groupEnd();
            })
            .catch(() => {
              this.console.log('SYNC PATCH SOFTERROR', item.$$patch);
              this.console.timeEnd(`SYNC PATCH ${item._id}`);
              this.syncing.done++;
              this.patchError.value = undefined;
            });
        } else if (item.$$patch && item.$$patch.deletedAt) {
          // offline deleted item
          this.syncing.todo++;
          this.console.time(`SYNC SOFTDELETE ${item._id}`);
          this._netpatch(item._id, {
            onClientAt: item.onClientAt,
            deletedAt: item.deletedAt,
          })
            .then(() => {
              this.syncing.done++;
              this.console.timeEnd(`SYNC SOFTDELETE ${item._id}`);
            })
            .catch(() => {
              this.console.log('SYNC SOFTDELETE SOFTERROR', item.$$patch);
              this.console.timeEnd(`SYNC SOFTDELETE ${item._id}`);
              this.syncing.done++;
              this.patchError.value = undefined;
            });
        }
      }
      if (index === stateItems.length - 1) {
        forEachReady();
      }
    });
  }

  // storage
  saveInStorage(items: ItemModel[]) {
    this.storage.setItem && this.storage.setItem(`@store/${this.service}`, JSON.stringify(items));
  }

  // feathers client communication
  _netcreate(data: CreateItemModel): Promise<ItemModel> {
    return new Promise((res, rej) => {
      this.service
        .create(data)
        .then((resp) => {
          this.setItems([resp]);
          this.isCreatePending.value = false;
          this.createError.value = undefined;
          res(resp);
        })
        .catch((e: Error) => {
          this.isCreatePending.value = false;
          this.createError.value = e;
          // @ts-ignore
          this.removeItem(data.uuid);
          rej(e);
        });
    });
  }

  _netfind(query: IFeathersQuery = {}): Promise<IServiceFindResponse> {
    return new Promise((res, rej) => {
      const queryParams = {
        ...query,
        ...(query.filter || {}),
      };
      delete queryParams.filter;
      this.isFindPending.value = true;
      this.service
        .find({ query: queryParams })
        .then((resp) => {
          this.setItems(resp.data);
          this.isFindPending.value = false;
          this.findError.value = undefined;
          res(resp);
        })
        .catch((e: Error) => {
          this.isFindPending.value = false;
          this.findError.value = e;
          rej(e);
        });
    });
  }

  _netget(id: string, params: IFeathersQuery = {}): Promise<ItemModel> {
    return new Promise((res, rej) => {
      this.service
        .get(id, params)
        .then((resp) => {
          this.setItems([resp]);
          this.isGetPending.value = false;
          this.getError.value = undefined;
          res(resp);
        })
        .catch((e: Error) => {
          this.isGetPending.value = false;
          this.getError.value = e;
          this.removeItem(id);
          rej(e);
        });
    });
  }

  _netupdate(id: string, data: Partial<ItemModel | IDefaultOfflineItemModel>, params = {}): Promise<ItemModel> {
    return new Promise((res, rej) => {
      this.service
        .update(id, data, params)
        .then((resp) => {
          this.setItems([resp]);
          this.isUpdatePending.value = false;
          this.updateError.value = undefined;
          res(resp);
        })
        .catch((e: Error) => {
          this.isUpdatePending.value = false;
          this.updateError.value = e;
          rej(e);
        });
    });
  }

  _netpatch(id: string, value: Partial<ItemModel | IDefaultOfflineItemModel>, params = {}): Promise<ItemModel> {
    return new Promise((res, rej) => {
      this.service
        .patch(id, value, params)
        .then((resp) => {
          this.setItems([resp]);
          this.isPatchPending.value = false;
          this.patchError.value = undefined;
          res(resp);
        })
        .catch((e: Error) => {
          this.isPatchPending.value = false;
          this.patchError.value = e;
          rej(e);
        });
    });
  }

  _netremove(id: string, params = {}): Promise<void> {
    return new Promise((res, rej) => {
      this.service
        .remove(id, params)
        .then((resp) => {
          if (resp && resp._id) {
            this.setItems([resp]);
          }
          this.isRemovePending.value = false;
          this.removeError.value = undefined;
          this.removeItem(id);
          res();
        })
        .catch((e: Error) => {
          this.removeError.value = e;
          this.isRemovePending.value = false;
          rej(e);
        });
    });
  }

  // event handling
  on(event: string, payload: ItemModel) {
    this.onEvent && this.onEvent(event, payload);
    this.console.log(this.serviceName, 'WebSocket Event', event, payload);
    switch (event) {
      case 'created':
      case 'patched':
      case 'updated':
        this.setItems([payload]);
        break;
      case 'removed':
        this.removeItem(payload._id || payload.uuid);
        break;
      default:
        break;
    }
  }
}
